<template>
  <b-modal
    v-model="modalVisible"
    @hidden="onHidden"
    @show="onOpen"
    size="xl"
    scrollable
    no-enforce-focus
  >
    <template #modal-title>
      <div class="d-flex align-items-center">
        <i class="fa fa-bolt mr-2 text-warning"></i>
        Live Events &mdash; <strong class="ml-1">{{ device.name }}</strong>
        <span class="ml-2 text-muted small">{{ device.serial }}</span>
        <b-badge
          v-if="particleDevice"
          :variant="particleDevice.status === 'online' ? 'success' : 'secondary'"
          class="ml-3"
        >
          <i :class="particleDevice.status === 'online' ? 'fa fa-circle' : 'fa fa-circle-o'" class="mr-1"></i>
          {{ particleDevice.status === 'online' ? 'Online' : 'Offline' }}
        </b-badge>
      </div>
    </template>

    <!-- Loading Particle device -->
    <div v-if="loadingDevice" class="text-center py-5">
      <b-spinner></b-spinner>
      <p class="mt-2 text-muted">{{ loadingMessage }}</p>
    </div>

    <!-- No matching Particle device found (even after auto-sync) -->
    <div v-else-if="!particleDevice" class="text-center py-5">
      <i class="fa fa-cloud fa-3x text-muted mb-3"></i>
      <h5 class="text-muted">Device Not Found</h5>
      <p class="text-muted">
        No Particle device matching <code>{{ device.serial }}</code> was found after syncing with Particle Cloud.
      </p>
    </div>

    <!-- Main content tabs -->
    <b-tabs v-else v-model="activeTab" @input="onTabChange" content-class="pt-3">

      <!-- ── Events ── -->
      <b-tab title="Live Events">
        <template #title>
          <i class="fa fa-rss mr-1"></i> Live Events
          <b-badge v-if="polling" variant="success" pill class="ml-1" style="font-size:0.65rem">●</b-badge>
        </template>

        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <b-button
              size="sm"
              :variant="polling ? 'outline-secondary' : 'outline-success'"
              @click="togglePolling"
              class="mr-2"
            >
              <i :class="polling ? 'fa fa-pause' : 'fa fa-play'"></i>
              {{ polling ? 'Pause' : 'Resume' }}
            </b-button>
            <b-button size="sm" variant="outline-primary" @click="fetchEvents" :disabled="loadingEvents">
              <i class="fa fa-refresh" :class="{ 'fa-spin': loadingEvents }"></i>
            </b-button>
          </div>
          <small class="text-muted" v-if="lastEventPoll">Last updated {{ lastEventPoll }}</small>
        </div>

        <div v-if="loadingEvents && events.length === 0" class="text-center py-4">
          <b-spinner small></b-spinner>
        </div>
        <div v-else-if="events.length === 0" class="text-center text-muted py-4">
          <i class="fa fa-inbox fa-2x mb-2"></i>
          <p>No recent events from this device</p>
        </div>
        <b-table
          v-else
          :items="events"
          :fields="eventFields"
          small
          striped
          hover
          responsive
          :tbody-tr-class="rowClass"
          class="mb-0 event-table"
        >
          <template #cell(eventName)="{ item }">
            <b-badge :variant="eventVariant(item.eventName)" class="event-badge">{{ item.eventName }}</b-badge>
          </template>
          <template #cell(data)="{ item }">
            <code class="event-data">{{ formatEventData(item.data) }}</code>
          </template>
          <template #cell(publishedAt)="{ item }">
            <small class="text-nowrap">{{ formatTime(item.publishedAt || item.created) }}</small>
          </template>
        </b-table>
      </b-tab>

      <!-- ── Commands ── -->
      <b-tab>
        <template #title>
          <i class="fa fa-terminal mr-1"></i> Commands
        </template>

        <!-- Available functions from device firmware -->
        <div class="mb-4">
          <div class="d-flex align-items-center mb-2">
            <p class="section-label mb-0">Available Functions</p>
            <b-button size="sm" variant="link" class="ml-auto p-0" @click="fetchFunctions" :disabled="loadingFunctions">
              <i class="fa fa-refresh" :class="{ 'fa-spin': loadingFunctions }"></i>
            </b-button>
          </div>

          <div v-if="loadingFunctions" class="text-muted small">
            <b-spinner small class="mr-1"></b-spinner> Querying device...
          </div>

          <div v-else-if="functionsConnected === false" class="text-muted small">
            <i class="fa fa-power-off mr-1"></i>
            Device is offline — functions unavailable. Commands can still be sent from the form below.
          </div>

          <div v-else-if="deviceFunctions.length === 0 && functionsConnected !== null" class="text-muted small">
            No functions registered on this device.
          </div>

          <div v-else-if="deviceFunctions.length">
            <b-button
              v-for="fn in deviceFunctions"
              :key="fn"
              variant="outline-primary"
              size="sm"
              class="mr-2 mb-2"
              :disabled="sendingCommand"
              @click="sendPresetCommand(fn, '')"
            >
              <i class="fa fa-terminal mr-1"></i>{{ fn }}
            </b-button>
          </div>

          <small v-if="lastCommandResult" :class="lastCommandResult.ok ? 'text-success' : 'text-danger'" class="d-block mt-1">
            <i :class="lastCommandResult.ok ? 'fa fa-check' : 'fa fa-times'" class="mr-1"></i>
            {{ lastCommandResult.message }}
          </small>
        </div>

        <!-- Custom command -->
        <p class="section-label">Custom Command</p>
        <b-form @submit.prevent="sendCustomCommand" class="mb-4">
          <b-row>
            <b-col md="4">
              <b-form-group label="Function Name" label-size="sm">
                <b-form-input
                  v-model="commandForm.name"
                  size="sm"
                  placeholder="e.g. config, setParam"
                  :disabled="sendingCommand"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Argument" label-size="sm">
                <b-form-input
                  v-model="commandForm.argument"
                  size="sm"
                  placeholder="optional argument string"
                  :disabled="sendingCommand"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="2" class="d-flex align-items-end pb-3">
              <b-button
                type="submit"
                variant="primary"
                size="sm"
                :disabled="sendingCommand || !commandForm.name"
                block
              >
                <b-spinner v-if="sendingCommand" small></b-spinner>
                <span v-else><i class="fa fa-paper-plane mr-1"></i>Send</span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>

        <!-- Command history -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <p class="section-label mb-0">Command History</p>
          <b-button size="sm" variant="link" @click="fetchCommands" :disabled="loadingCommands">
            <i class="fa fa-refresh" :class="{ 'fa-spin': loadingCommands }"></i>
          </b-button>
        </div>
        <div v-if="loadingCommands && commands.length === 0" class="text-center py-3">
          <b-spinner small></b-spinner>
        </div>
        <div v-else-if="commands.length === 0" class="text-center text-muted py-3">
          No commands sent yet
        </div>
        <b-table
          v-else
          :items="commands"
          :fields="commandFields"
          small
          striped
          hover
          responsive
          class="mb-0"
        >
          <template #cell(status)="{ item }">
            <b-badge :variant="commandVariant(item.status)">{{ item.status }}</b-badge>
          </template>
          <template #cell(commandName)="{ item }">
            <code>{{ item.commandName }}</code>
          </template>
          <template #cell(argument)="{ item }">
            <code v-if="item.argument" class="text-muted">{{ item.argument }}</code>
            <span v-else class="text-muted">—</span>
          </template>
          <template #cell(requestedAt)="{ item }">
            <small>{{ formatTime(item.sentAt || item.requestedAt) }}</small>
          </template>
        </b-table>
      </b-tab>

      <!-- ── Device Info ── -->
      <b-tab>
        <template #title>
          <i class="fa fa-info-circle mr-1"></i> Device Info
        </template>

        <!-- Last-known state -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <p class="section-label mb-0">Last Known State</p>
          <b-button size="sm" variant="outline-secondary" @click="fetchState" :disabled="loadingState">
            <i class="fa fa-refresh" :class="{ 'fa-spin': loadingState }"></i> Refresh
          </b-button>
        </div>

        <div v-if="loadingState" class="text-center py-3"><b-spinner small></b-spinner></div>
        <div v-else-if="flatState && Object.keys(flatState).length">
          <b-row>
            <b-col
              v-for="(value, key) in flatState"
              :key="key"
              sm="6"
              md="4"
              class="mb-2"
            >
              <div class="state-field p-2 border rounded">
                <small class="text-muted text-uppercase d-block" style="font-size:0.7rem; letter-spacing:0.04em">{{ key }}</small>
                <span class="font-weight-bold">{{ value }}</span>
              </div>
            </b-col>
          </b-row>
        </div>
        <div v-else class="text-muted text-center py-3">No state data available</div>

        <!-- Particle device metadata -->
        <hr class="my-3">
        <p class="section-label">Particle Device</p>
        <b-row class="mb-3">
          <b-col sm="6" md="4" class="mb-2" v-if="particleDevice.particleId">
            <div class="state-field p-2 border rounded">
              <small class="text-muted text-uppercase d-block" style="font-size:0.7rem; letter-spacing:0.04em">Particle ID</small>
              <code style="font-size:0.8rem">{{ particleDevice.particleId }}</code>
            </div>
          </b-col>
          <b-col sm="6" md="4" class="mb-2" v-if="particleDevice.firmwareVersion">
            <div class="state-field p-2 border rounded">
              <small class="text-muted text-uppercase d-block" style="font-size:0.7rem; letter-spacing:0.04em">Firmware</small>
              <span class="font-weight-bold">{{ particleDevice.firmwareVersion }}</span>
            </div>
          </b-col>
          <b-col sm="6" md="4" class="mb-2" v-if="particleDevice.lastHeardAt">
            <div class="state-field p-2 border rounded">
              <small class="text-muted text-uppercase d-block" style="font-size:0.7rem; letter-spacing:0.04em">Last Heard</small>
              <span class="font-weight-bold">{{ formatTime(particleDevice.lastHeardAt) }}</span>
            </div>
          </b-col>
          <b-col sm="6" md="4" class="mb-2" v-if="particleDevice.lastIpAddress">
            <div class="state-field p-2 border rounded">
              <small class="text-muted text-uppercase d-block" style="font-size:0.7rem; letter-spacing:0.04em">Last IP</small>
              <span class="font-weight-bold">{{ particleDevice.lastIpAddress }}</span>
            </div>
          </b-col>
          <b-col sm="6" md="4" class="mb-2" v-if="particleDevice.lastIccid">
            <div class="state-field p-2 border rounded">
              <small class="text-muted text-uppercase d-block" style="font-size:0.7rem; letter-spacing:0.04em">ICCID</small>
              <span class="font-weight-bold">{{ particleDevice.lastIccid }}</span>
            </div>
          </b-col>
          <b-col sm="6" md="4" class="mb-2" v-if="particleDevice.serialNumber">
            <div class="state-field p-2 border rounded">
              <small class="text-muted text-uppercase d-block" style="font-size:0.7rem; letter-spacing:0.04em">Serial</small>
              <span class="font-weight-bold">{{ particleDevice.serialNumber }}</span>
            </div>
          </b-col>
        </b-row>

        <hr class="my-3">

        <!-- Variable reader -->
        <p class="section-label">Read Variable</p>
        <b-form @submit.prevent="readVariable" class="mb-3">
          <b-input-group size="sm" style="max-width:440px">
            <b-form-input
              v-model="variableName"
              placeholder="Variable name (e.g. network_info)"
              :disabled="readingVariable"
            ></b-form-input>
            <b-input-group-append>
              <b-button variant="outline-secondary" @click="readCellularInfo" :disabled="readingVariable">
                <i class="fa fa-signal mr-1"></i> Cellular
              </b-button>
              <b-button type="submit" variant="outline-primary" :disabled="readingVariable || !variableName">
                <b-spinner v-if="readingVariable" small></b-spinner>
                <span v-else><i class="fa fa-eye mr-1"></i> Read</span>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form>

        <div v-if="variableError" class="alert alert-danger py-2 small">{{ variableError }}</div>
        <pre v-else-if="variableResult !== null" class="variable-result">{{ variableResult }}</pre>
      </b-tab>

    </b-tabs>

    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        size="sm"
        @click="syncAndReload"
        :disabled="syncing"
        class="mr-auto"
      >
        <b-spinner v-if="syncing" small class="mr-1"></b-spinner>
        <i v-else class="fa fa-cloud-download mr-1"></i>
        Sync Particle
      </b-button>
      <b-button variant="secondary" @click="$emit('close')">Close</b-button>
    </template>
  </b-modal>
</template>

<script>
import { getAccessToken } from '@/lib/auth'

const API_BASE = (process.env.VUE_APP_API_ENDPOINT || 'https://apiv2.leaf.ai/graphql').replace('/graphql', '')
const POLL_INTERVAL_MS = 6000

export default {
  name: 'DeviceActionsModal',
  props: {
    device: { type: Object, required: true },
    visible: { type: Boolean, default: false }
  },
  data () {
    return {
      loadingDevice: false,
      loadingMessage: 'Connecting to Particle Cloud...',
      particleDevice: null,
      syncing: false,
      activeTab: 0,

      // Events
      events: [],
      loadingEvents: false,
      polling: false,
      pollTimer: null,
      lastEventPoll: null,
      eventFields: [
        { key: 'eventName',   label: 'Event',    sortable: true },
        { key: 'data',        label: 'Data' },
        { key: 'publishedAt', label: 'Time',     sortable: true }
      ],

      // Commands
      commands: [],
      loadingCommands: false,
      deviceFunctions: [],
      loadingFunctions: false,
      functionsConnected: null,
      sendingCommand: false,
      lastCommandResult: null,
      commandForm: { name: '', argument: '' },
      commandFields: [
        { key: 'commandName',  label: 'Command' },
        { key: 'argument',     label: 'Argument' },
        { key: 'status',       label: 'Status',  sortable: true },
        { key: 'requestedAt',  label: 'Sent' }
      ],

      // State & variables
      deviceState: null,
      flatState: null,
      loadingState: false,
      variableName: '',
      variableResult: null,
      variableError: null,
      readingVariable: false
    }
  },
  computed: {
    modalVisible: {
      get () { return this.visible },
      set (v) { if (!v) this.$emit('close') }
    }
  },
  watch: {
    visible (val) {
      if (val) this.onOpen()
    }
  },
  methods: {
    // ── Lifecycle ──────────────────────────────────────────────────────────────

    async onOpen () {
      this.reset()
      await this.loadParticleDevice()
      if (this.particleDevice) {
        this.startPolling()
        this.fetchCommands()
        this.fetchFunctions()
        this.fetchState()
      }
    },

    onHidden () {
      this.stopPolling()
      this.$emit('close')
    },

    onTabChange (tab) {
      if (tab === 0 && this.particleDevice && !this.polling) {
        this.startPolling()
      } else if (tab !== 0) {
        this.stopPolling()
      }
      if (tab === 1 && this.particleDevice && this.deviceFunctions.length === 0 && this.functionsConnected === null) {
        this.fetchFunctions()
      }
    },

    reset () {
      this.particleDevice = null
      this.events = []
      this.commands = []
      this.deviceFunctions = []
      this.functionsConnected = null
      this.deviceState = null
      this.flatState = null
      this.lastCommandResult = null
      this.variableResult = null
      this.variableError = null
      this.variableName = ''
      this.commandForm = { name: '', argument: '' }
      this.activeTab = 0
    },

    // ── HTTP helper ────────────────────────────────────────────────────────────

    async api (method, path, body) {
      const opts = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`
        }
      }
      if (body) opts.body = JSON.stringify(body)
      const res = await fetch(`${API_BASE}${path}`, opts)
      if (!res.ok) {
        let errMsg = `HTTP ${res.status}`
        try {
          const json = await res.json()
          if (json && json.error) errMsg = json.error
        } catch (e) { /* ignore parse failure */ }
        throw new Error(errMsg)
      }
      return res.json()
    },

    // ── Particle device lookup ─────────────────────────────────────────────────

    findInList (devices) {
      const serial = (this.device.serial || '').toLowerCase()
      return (devices || []).find(function (pd) {
        return (pd.particleId   && pd.particleId.toLowerCase()   === serial) ||
               (pd.serialNumber && pd.serialNumber.toLowerCase() === serial) ||
               (pd.name         && pd.name.toLowerCase()         === serial)
      }) || null
    },

    async loadParticleDevice () {
      this.loadingDevice = true
      this.loadingMessage = 'Looking up device...'
      try {
        const devices = await this.api('GET', '/api/particle/devices')
        this.particleDevice = this.findInList(devices)

        if (!this.particleDevice) {
          this.loadingMessage = 'Syncing from Particle Cloud...'
          await this.api('POST', '/api/particle/sync')
          const synced = await this.api('GET', '/api/particle/devices')
          this.particleDevice = this.findInList(synced)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load Particle devices', e)
      } finally {
        this.loadingDevice = false
      }
    },

    async syncAndReload () {
      this.syncing = true
      try {
        await this.api('POST', '/api/particle/sync')
        const devices = await this.api('GET', '/api/particle/devices')
        this.particleDevice = this.findInList(devices)
        if (this.particleDevice) {
          this.startPolling()
          this.fetchCommands()
          this.fetchFunctions()
          this.fetchState()
          this.$bvToast.toast('Synced from Particle Cloud.', {
            title: 'Synced', variant: 'success', solid: true, autoHideDelay: 3000
          })
        } else {
          this.$bvToast.toast('Device not found after sync.', {
            title: 'Not Found', variant: 'warning', solid: true
          })
        }
      } catch (e) {
        this.$bvToast.toast('Sync failed: ' + e.message, {
          title: 'Error', variant: 'danger', solid: true
        })
      } finally {
        this.syncing = false
      }
    },

    // ── Events ─────────────────────────────────────────────────────────────────

    startPolling () {
      this.polling = true
      this.fetchEvents()
      this.pollTimer = setInterval(this.fetchEvents, POLL_INTERVAL_MS)
    },

    stopPolling () {
      this.polling = false
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },

    togglePolling () {
      if (this.polling) {
        this.stopPolling()
      } else {
        this.startPolling()
      }
    },

    async fetchEvents () {
      if (!this.particleDevice) return
      this.loadingEvents = true
      try {
        const data = await this.api('GET', `/api/particle/devices/${this.particleDevice.id}/events?limit=50`)
        this.events = Array.isArray(data) ? data : (data.events || [])
        this.lastEventPoll = new Date().toLocaleTimeString()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('fetchEvents failed', e)
      } finally {
        this.loadingEvents = false
      }
    },

    // ── Commands ───────────────────────────────────────────────────────────────

    async fetchCommands () {
      if (!this.particleDevice) return
      this.loadingCommands = true
      try {
        const data = await this.api('GET', `/api/particle/devices/${this.particleDevice.id}/commands?limit=50`)
        this.commands = Array.isArray(data) ? data : (data.commands || [])
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('fetchCommands failed', e)
      } finally {
        this.loadingCommands = false
      }
    },

    async fetchFunctions () {
      if (!this.particleDevice) return
      this.loadingFunctions = true
      this.deviceFunctions = []
      this.functionsConnected = null
      try {
        const data = await this.api('GET', `/api/particle/devices/${this.particleDevice.id}/functions`)
        this.functionsConnected = data.connected === true
        this.deviceFunctions = Array.isArray(data.functions) ? data.functions : []
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('fetchFunctions failed', e)
        this.functionsConnected = false
      } finally {
        this.loadingFunctions = false
      }
    },

    prefillCommand (fn) {
      this.commandForm.name = fn
      this.commandForm.argument = ''
    },

    async sendPresetCommand (commandName, argument) {
      await this.executeCommand(commandName, argument)
    },

    async sendCustomCommand () {
      if (!this.commandForm.name) return
      await this.executeCommand(this.commandForm.name, this.commandForm.argument)
      this.commandForm.name = ''
      this.commandForm.argument = ''
    },

    async executeCommand (commandName, argument) {
      this.sendingCommand = true
      this.lastCommandResult = null
      try {
        const result = await this.api('POST', `/api/particle/devices/${this.particleDevice.id}/commands`, {
          commandName,
          argument: argument || '',
          useCorrelation: true
        })
        const cmd = result.command || result
        const returnVal = result.particleReturnValue
        const detail = returnVal !== undefined ? ` (return: ${returnVal})` : ''
        this.lastCommandResult = { ok: true, message: `"${commandName}" ${cmd.status || 'sent'}${detail}` }
        this.$bvToast.toast(`Command "${commandName}" sent successfully.`, {
          title: 'Command Sent', variant: 'success', solid: true, autoHideDelay: 3000
        })
        await this.fetchCommands()
      } catch (e) {
        this.lastCommandResult = { ok: false, message: e.message }
        this.$bvToast.toast(`Failed to send "${commandName}": ${e.message}`, {
          title: 'Command Failed', variant: 'danger', solid: true
        })
      } finally {
        this.sendingCommand = false
      }
    },

    // ── Device State & Variables ───────────────────────────────────────────────

    async fetchState () {
      if (!this.particleDevice) return
      this.loadingState = true
      try {
        const data = await this.api('GET', `/api/particle/devices/${this.particleDevice.id}/state`)
        this.deviceState = data
        // Omit internal relation fields from display
        const displayState = Object.assign({}, data || {})
        delete displayState.id
        delete displayState.deviceId
        this.flatState = this.flattenState(displayState)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('fetchState failed', e)
      } finally {
        this.loadingState = false
      }
    },

    flattenState (obj, prefix = '') {
      if (!obj || typeof obj !== 'object') return {}
      const result = {}
      for (const [k, v] of Object.entries(obj)) {
        const key = prefix ? `${prefix}.${k}` : k
        if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
          Object.assign(result, this.flattenState(v, key))
        } else {
          result[key] = Array.isArray(v) ? JSON.stringify(v) : String(v != null ? v : '—')
        }
      }
      return result
    },

    async readVariable () {
      if (!this.variableName) return
      await this.doReadVariable(this.variableName)
    },

    async readCellularInfo () {
      this.variableName = 'network_info'
      await this.doReadVariable('network_info')
    },

    async doReadVariable (varName) {
      this.readingVariable = true
      this.variableResult = null
      this.variableError = null
      try {
        const data = await this.api('GET', `/api/particle/devices/${this.particleDevice.id}/variable/${encodeURIComponent(varName)}`)
        this.variableResult = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data)
      } catch (e) {
        this.variableError = e.message
      } finally {
        this.readingVariable = false
      }
    },

    // ── Display helpers ────────────────────────────────────────────────────────

    eventVariant (name) {
      if (!name) return 'secondary'
      const n = name.toLowerCase()
      if (n.includes('error') || n.includes('fail')) return 'danger'
      if (n.includes('warn')) return 'warning'
      if (n.includes('connect') || n.includes('online') || n.includes('publish')) return 'success'
      if (n.includes('reset') || n.includes('boot')) return 'info'
      return 'primary'
    },

    commandVariant (status) {
      if (!status) return 'secondary'
      switch (status.toLowerCase()) {
        case 'succeeded':
        case 'acknowledged': return 'success'
        case 'failed':       return 'danger'
        case 'sent':         return 'info'
        case 'pending':      return 'warning'
        default:             return 'secondary'
      }
    },

    formatEventData (data) {
      if (!data) return '—'
      try {
        const parsed = JSON.parse(data)
        return JSON.stringify(parsed, null, 2)
      } catch (e) {
        return data
      }
    },

    formatTime (ts) {
      if (!ts) return '—'
      return new Date(ts).toLocaleString()
    },

    rowClass (item) {
      if (!item || !item.eventName) return ''
      const n = item.eventName.toLowerCase()
      if (n.includes('error') || n.includes('fail')) return 'table-danger'
      return ''
    }
  },

  beforeDestroy () {
    this.stopPolling()
  }
}
</script>

<style scoped>
.section-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 0.6rem;
}

.event-badge {
  font-size: 0.75rem;
  white-space: nowrap;
}

.event-data {
  font-size: 0.78rem;
  white-space: pre-wrap;
  word-break: break-all;
  max-width: 420px;
  display: block;
}

.event-table {
  font-size: 0.85rem;
}

.variable-result {
  background: #1e1e1e;
  color: #d4d4d4;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-size: 0.82rem;
  max-height: 260px;
  overflow: auto;
}

.state-field {
  background: #f8f9fa;
}

</style>
